/**
 * Claim-suggestion decision actions (CT-5095) — the Train Me footer.
 *
 * Contract: My Work API (`my_work.yaml`), base `/v1/my-work`, all POST + camelCase.
 * Workspace context + the calling user come from the token — never in the body.
 *
 *   - Accept a pending suggestion  → POST /v1/my-work/claim-suggestion/accept
 *       body { suggestionUuid }, response { suggestionUuid, claimUuid, resolvedAt, resolvedBy }.
 *   - Decline a pending suggestion → POST /v1/my-work/claim-suggestion/decline
 *       body { suggestionUuid, reason? }, same response shape.
 *   - Propose a change (counter)   → POST /v1/my-work/claim-suggestion/suggest
 *       body { claimUuid, suggestedText?, rationale?, riskLevel?, ... },
 *       response { suggestionUuid, claimUuid, createdAt, createdBy }.
 *
 * All three verified live 2026-09-03 (200 CLM_SGT_ACCEPTED / _DECLINED / _CREATED).
 *
 * GAP-1 (list fan-out): accept/decline REQUIRE a `suggestionUuid`. The inbox list
 * resolves it from `metadata.suggestions[]` (see `pendingSuggestionUuid` in
 * `mapSourceToEvent.js`). The preview endpoint still does not expose it — only
 * `suggest` returns a fresh uuid. After a counter-suggestion, the provider updates
 * `detail.suggestionUuid` locally from the suggest response.
 */

import { postGsAction } from './gsAction.js';

/**
 * Accept (promote) a pending claim suggestion.
 * @param {{ suggestionUuid: string }} params
 * @returns {Promise<Object|null>} unwrapped response, or `null` when the uuid is missing.
 */
export async function acceptClaimSuggestion({ suggestionUuid } = {}) {
  if (!suggestionUuid) return null;
  return postGsAction(
    '/v1/my-work/claim-suggestion/accept',
    { suggestionUuid },
    'Failed to accept suggestion',
  );
}

/**
 * Decline (reject) a pending claim suggestion.
 * @param {{ suggestionUuid: string, reason?: string }} params
 * @returns {Promise<Object|null>} unwrapped response, or `null` when the uuid is missing.
 */
export async function declineClaimSuggestion({ suggestionUuid, reason } = {}) {
  if (!suggestionUuid) return null;
  const payload = { suggestionUuid };
  if (reason != null) payload.reason = reason;
  return postGsAction(
    '/v1/my-work/claim-suggestion/decline',
    payload,
    'Failed to decline suggestion',
  );
}

/**
 * Propose a change on a claim (Train Me "edit then promote" → a counter-suggestion).
 * Returns a fresh `suggestionUuid` in the body — the only endpoint that produces one.
 * @param {{ claimUuid: string, suggestedText?: string, suggestedContext?: string,
 *           suggestedSource?: string, rationale?: string, confidence?: number,
 *           riskLevel?: 'Low'|'Medium'|'High', polarity?: 'positive'|'negative'|'neutral',
 *           notes?: string, notifyRecipients?: Object }} params
 * @returns {Promise<Object|null>} unwrapped response, or `null` when the claimUuid is missing.
 */
export async function suggestClaimChange({
  claimUuid,
  suggestedText,
  suggestedContext,
  suggestedSource,
  rationale,
  confidence,
  riskLevel,
  polarity,
  notes,
  notifyRecipients,
} = {}) {
  if (!claimUuid) return null;
  const payload = { claimUuid };
  if (suggestedText != null) payload.suggestedText = suggestedText;
  if (suggestedContext != null) payload.suggestedContext = suggestedContext;
  if (suggestedSource != null) payload.suggestedSource = suggestedSource;
  if (rationale != null) payload.rationale = rationale;
  if (confidence != null) payload.confidence = confidence;
  if (riskLevel) payload.riskLevel = riskLevel;
  if (polarity) payload.polarity = polarity;
  if (notes != null) payload.notes = notes;
  if (notifyRecipients != null) payload.notifyRecipients = notifyRecipients;
  return postGsAction(
    '/v1/my-work/claim-suggestion/suggest',
    payload,
    'Failed to submit suggestion',
  );
}
